import { useCallback, useEffect, useState } from "react";
import { Laptop, LogOut, ShieldCheck, Smartphone } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ApiError } from "../../lib/api";
import { adminDevicesApi, type AdminDevice, type AdminSecuritySettings } from "../../lib/adminDevicesApi";
import { PageHeader } from "../../components/ui/PageHeader";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { EmptyStateCard } from "../../components/ui/EmptyStateCard";
import { SkeletonList } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";

function formatWhen(iso: string | null): string {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown";
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const SETTING_FIELDS: { key: keyof AdminSecuritySettings; label: string; hint: string; min: number; max: number }[] = [
  { key: "idle_lock_minutes", label: "Lock after idle (minutes)", hint: "Password only to continue", min: 5, max: 1440 },
  { key: "trust_days", label: "Trust a device for (days)", hint: "Then a code is needed again", min: 1, max: 365 },
  { key: "unlock_max_failures", label: "Wrong passwords before sign-out", hint: "On the lock screen", min: 3, max: 20 },
];

export function AdminDevicesPage() {
  const { tokens, getAccessToken, logout } = useAuth();
  const [devices, setDevices] = useState<AdminDevice[] | null>(null);
  const [settings, setSettings] = useState<AdminSecuritySettings | null>(null);
  const [draft, setDraft] = useState<Partial<Record<keyof AdminSecuritySettings, string>>>({});
  const [error, setError] = useState("");
  const [revoking, setRevoking] = useState<AdminDevice | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const token = await getAccessToken();
      if (!token) return;
      const res = await adminDevicesApi.list(token);
      setDevices(res.devices);
      setSettings(res.settings);
      setDraft({});
    } catch {
      setError("Could not load your devices. Check your connection and try again.");
    }
  }, [getAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke() {
    if (!revoking) return;
    setBusy(true);
    try {
      const token = (await getAccessToken()) ?? tokens?.accessToken ?? "";
      await adminDevicesApi.revoke(revoking.id, token);
      toast.success("Device revoked. It will need a code to sign in again.");
      const wasCurrent = revoking.isCurrent;
      setRevoking(null);
      if (wasCurrent) {
        logout(true);
        return;
      }
      await load();
    } catch {
      toast.error("Could not revoke this device. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function logoutEverywhere() {
    setBusy(true);
    try {
      const token = (await getAccessToken()) ?? tokens?.accessToken ?? "";
      await adminDevicesApi.logoutEverywhere(token);
      toast.success("Signed out everywhere.");
      setConfirmAll(false);
      logout(true);
    } catch {
      toast.error("Could not sign out everywhere. Try again.");
      setBusy(false);
    }
  }

  async function saveSettings() {
    const body: Partial<AdminSecuritySettings> = {};
    for (const f of SETTING_FIELDS) {
      const raw = draft[f.key];
      if (raw === undefined || raw === "") continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < f.min || n > f.max) {
        toast.error(`${f.label} must be between ${f.min} and ${f.max}.`);
        return;
      }
      body[f.key] = Math.round(n);
    }
    if (Object.keys(body).length === 0) return;
    setSaving(true);
    try {
      const token = (await getAccessToken()) ?? tokens?.accessToken ?? "";
      const next = await adminDevicesApi.saveSettings(body, token);
      setSettings(next);
      setDraft({});
      toast.success("Security settings saved.");
    } catch (err) {
      toast.error(err instanceof ApiError && err.status === 403 ? "You do not have permission to change this." : "Could not save settings. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Devices & sessions"
        description="Devices that can sign in with your password only. Anything not listed needs a Telegram code."
        actions={
          <Button variant="destructive" icon={<LogOut size={16} />} onClick={() => setConfirmAll(true)}>
            Log out everywhere
          </Button>
        }
      />

      {error && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-tile bg-destructive/10 p-3 text-sm text-destructive">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      )}

      {devices === null && !error ? (
        <SkeletonList count={3} />
      ) : devices && devices.length === 0 ? (
        <EmptyStateCard
          icon={ShieldCheck}
          title="No trusted devices"
          message="Sign in with a code on a device to trust it."
        />
      ) : (
        <ul className="space-y-3">
          {devices?.map((d) => {
            const mobile = /android|iphone|ipad|ios/i.test(`${d.name} ${d.userAgent ?? ""}`);
            const Icon = mobile ? Smartphone : Laptop;
            return (
              <li key={d.id}>
                <Card className="flex flex-wrap items-center gap-3 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-accent-soft text-primary">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
                      <span className="truncate">{d.name}</span>
                      {d.isCurrent && <Badge>This device</Badge>}
                      {d.locked && <Badge>Locked</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last active {formatWhen(d.lastActiveAt)}
                      {d.lastIp ? ` · IP ${d.lastIp}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Trusted until {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString() : "unknown"}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setRevoking(d)}>
                    Revoke
                  </Button>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {settings && (
        <Card className="space-y-4 p-4">
          <h2 className="text-base font-semibold text-foreground">Security settings</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {SETTING_FIELDS.map((f) => (
              <div key={f.key}>
                <label htmlFor={`sec-${f.key}`} className="mb-1 block text-sm font-semibold text-foreground">
                  {f.label}
                </label>
                <input
                  id={`sec-${f.key}`}
                  type="number"
                  inputMode="numeric"
                  min={f.min}
                  max={f.max}
                  value={draft[f.key] ?? String(settings[f.key])}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => void saveSettings()} loading={saving} loadingText="Saving…" disabled={Object.keys(draft).length === 0}>
            Save settings
          </Button>
        </Card>
      )}

      <ConfirmDialog
        isOpen={Boolean(revoking)}
        title="Revoke this device?"
        message={
          revoking?.isCurrent
            ? "This is the device you are using. You will be signed out and need a code next time."
            : "It will be signed out and need a Telegram code to sign in again."
        }
        variant="destructive"
        confirmText="Revoke"
        loading={busy}
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}
      />
      <ConfirmDialog
        isOpen={confirmAll}
        title="Log out everywhere?"
        message="Every device, including this one, will be signed out and need a Telegram code next time."
        variant="destructive"
        confirmText="Log out everywhere"
        loading={busy}
        onConfirm={logoutEverywhere}
        onCancel={() => setConfirmAll(false)}
      />
    </div>
  );
}
